import {createContext, ReactNode, useCallback, useContext, useState} from 'react'

// Groups the viewer has opened or folded by hand, remembered in this browser only. A group with no
// entry here shows its default (the group's `defaultOpen`).
const CHOICES_KEY = 'hrm.sidebar.groups'

type Choices = Record<string, boolean>

const readChoices = (): Choices => {
  try {
    const stored = JSON.parse(localStorage.getItem(CHOICES_KEY) ?? '{}')
    return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {}
  } catch {
    return {}
  }
}

const writeChoices = (choices: Choices) => {
  try {
    localStorage.setItem(CHOICES_KEY, JSON.stringify(choices))
  } catch {
    // Storage unavailable: the fold still works, it just isn't remembered.
  }
}

type SidebarGroupsContextType = {
  isOpen: (key: string, byDefault: boolean) => boolean
  setOpen: (key: string, open: boolean) => void
  reveal: (key: string) => void
}

const SidebarGroupsContext = createContext<SidebarGroupsContextType>({
  isOpen: (_key, byDefault) => byDefault,
  setOpen: () => {},
  reveal: () => {},
})

export const useSidebarGroups = () => useContext(SidebarGroupsContext)

/**
 * Open/closed state of the sidebar groups. A group starts at its default (only Employee Self
 * Service is open); a click on its heading opens or folds it, and that choice is remembered.
 *
 * Arriving on a page inside a folded group reveals it for this visit only. It is not written back,
 * or every group the viewer had ever passed through would stay open for good.
 *
 * Kept in React rather than left to Metronic's accordion script, which toggles a `show` class that
 * React's next render of the item's className would silently put back.
 */
export const SidebarGroupsProvider = ({children}: {children: ReactNode}) => {
  const [choices, setChoices] = useState<Choices>(readChoices)
  const [revealed, setRevealed] = useState<Set<string>>(() => new Set())

  const isOpen = useCallback(
    (key: string, byDefault: boolean) => revealed.has(key) || (choices[key] ?? byDefault),
    [choices, revealed]
  )

  const setOpen = useCallback((key: string, open: boolean) => {
    setChoices((prev) => {
      const next = {...prev, [key]: open}
      writeChoices(next)
      return next
    })
    if (!open) {
      setRevealed((prev) => {
        if (!prev.has(key)) return prev
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }, [])

  // Stable identity: the caller's effect must fire on arriving in the group, not on every fold.
  const reveal = useCallback((key: string) => {
    setRevealed((prev) => (prev.has(key) ? prev : new Set(prev).add(key)))
  }, [])

  return (
    <SidebarGroupsContext.Provider value={{isOpen, setOpen, reveal}}>
      {children}
    </SidebarGroupsContext.Provider>
  )
}
